import React, { useMemo } from 'react';
import {
  Box,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Divider,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  Autocomplete
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { COMPETITION_STATUSES } from '../../constants/competitionConstants';

const SANCTION_ORGS = [
  { value: 'USAG', label: 'USA Gymnastics' },
  { value: 'AAU', label: 'Amateur Athletic Union' },
  { value: 'NCAA', label: 'National Collegiate Athletic Association' },
  { value: 'OTHER', label: 'Other' }
];

// Common US timezones for quick access
const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu'
];

const CompetitionSettingsForm = ({ competition, onUpdate }) => {
  // Get browser timezone as default
  const browserTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return 'America/New_York';
    }
  }, []);

  // Get all available timezones, with common ones first
  const allTimezones = useMemo(() => {
    try {
      const supported = Intl.supportedValuesOf('timeZone');
      // Put common timezones first, then the rest
      const others = supported.filter(tz => !COMMON_TIMEZONES.includes(tz));
      return [...COMMON_TIMEZONES, ...others];
    } catch (e) {
      // Fallback if Intl.supportedValuesOf not available
      return COMMON_TIMEZONES;
    }
  }, []);

  const handleChange = (field) => (event) => {
    onUpdate({
      ...competition,
      [field]: event.target.value
    });
  };

  const handleTimezoneChange = (event, newValue) => {
    onUpdate({
      ...competition,
      timezone: newValue || browserTimezone
    });
  };

  const handleColorChange = (field) => (newColor) => {
    // Ensure the color is a valid hex color
    const validColor = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newColor) 
      ? newColor 
      : field === 'primary' ? '#2196f3' : '#f50057';

    onUpdate({
      ...competition,
      colors: {
        ...(competition.colors || {}),
        [field]: validColor
      }
    });
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpdate({
          ...competition,
          logo: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box sx={{ mt: 4 }} data-ui="form-competition-settings">
      <Grid container spacing={4}>
        {/* Administrative Section */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Administrative Information
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  data-ui="form-settings-director"
                  fullWidth
                  label="Meet Director"
                  value={competition.director || ''}
                  onChange={handleChange('director')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  data-ui="form-settings-organization"
                  fullWidth
                  label="Organization"
                  value={competition.organization || ''}
                  onChange={handleChange('organization')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  data-ui="form-settings-sanction-org"
                  select
                  fullWidth
                  label="Sanction Organization"
                  value={competition.sanctionOrg || ''}
                  onChange={handleChange('sanctionOrg')}
                  onKeyDown={(event) => {
                    // Handle first letter navigation
                    const key = event.key.toUpperCase();
                    if (/^[A-Z]$/.test(key)) {
                      event.preventDefault();
                      // Find the first option that starts with the pressed letter
                      const option = SANCTION_ORGS.find(org => 
                        org.label.toUpperCase().startsWith(key)
                      );
                      if (option) {
                        onUpdate({
                          ...competition,
                          sanctionOrg: option.value
                        });
                      }
                    }
                  }}
                >
                  {SANCTION_ORGS.map((org) => (
                    <MenuItem key={org.value} value={org.value}>
                      {org.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  data-ui="form-settings-sanction-id"
                  fullWidth
                  label="Sanction Number"
                  value={competition.sanctionId || ''}
                  onChange={handleChange('sanctionId')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  data-ui="form-settings-timezone"
                  options={allTimezones}
                  value={competition.timezone || browserTimezone}
                  onChange={handleTimezoneChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Competition Timezone"
                      helperText="All session times will be in this timezone"
                    />
                  )}
                  getOptionLabel={(option) => {
                    // Format timezone nicely (e.g., "America/New_York" -> "America/New York (EST)")
                    try {
                      const formatted = option.replace(/_/g, ' ');
                      const date = new Date();
                      const shortName = new Intl.DateTimeFormat('en-US', {
                        timeZone: option,
                        timeZoneName: 'short'
                      }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value;
                      return shortName ? `${formatted} (${shortName})` : formatted;
                    } catch (e) {
                      return option;
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Appearance Section */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Appearance
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <MuiColorInput
                  data-ui="form-settings-primary-color"
                  fullWidth
                  label="Primary Color"
                  value={competition.colors?.primary || '#2196f3'}
                  onChange={handleColorChange('primary')}
                  format="hex"
                  isAlphaHidden
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiColorInput
                  data-ui="form-settings-secondary-color"
                  fullWidth
                  label="Secondary Color"
                  value={competition.colors?.secondary || '#f50057'}
                  onChange={handleColorChange('secondary')}
                  format="hex"
                  isAlphaHidden
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  data-ui="btn-upload-logo"
                  variant="outlined"
                  component="label"
                  fullWidth
                >
                  Upload Logo
                  <input
                    data-ui="input-logo-file"
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </Button>
                {competition.logo && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img 
                      src={competition.logo} 
                      alt="Competition Logo" 
                      style={{ maxHeight: 100 }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Registration Settings */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Registration Settings
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  data-ui="form-settings-registration-deadline"
                  fullWidth
                  type="datetime-local"
                  label="Registration Deadline"
                  value={competition.registrationDeadline || ''}
                  onChange={handleChange('registrationDeadline')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  data-ui="form-settings-status"
                  select
                  fullWidth
                  label="Competition Status"
                  value={competition.status || 'draft'}
                  onChange={handleChange('status')}
                >
                  {Object.values(COMPETITION_STATUSES).map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompetitionSettingsForm;
